import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RxHome } from 'react-icons/rx';
import { getAdminClientsCached, getStoresDataCached } from '../../utils/metadataCache';
import { getBreadcrumbName, subscribeBreadcrumbNames } from '../../utils/breadcrumbNames';

const SEGMENT_LABELS = {
  stores: 'Stores',
  onboard: 'Onboarding',
  'webhook-logs': 'Webhook Logs',
  clients: 'Clients',
  client: 'Client',
  users: 'Users',
  account: 'Account',
  dashboard: 'Dashboard',
  invoices: 'Invoices',
  invoice: 'Invoice',
  ledger: 'Ledger',
  orders: 'Orders',
  order: 'Order',
  asn: 'ASN',
  new: 'New',
  product: 'Product',
  products: 'Products',
  purchases: 'Purchases',
  tools: 'Tools',
  webhooks: 'Webhooks',
  events: 'Events',
  'bulk-owner': 'Bulk Owner',
  query: 'Query',
  search: 'Search',
  admin: 'Admin',
  owner: 'Owner',
  inventory: 'Inventory',
  'my-store': 'My Store',
  'my-clients': 'My Clients',
  transfers: 'Transfers',
  billing: 'Billing',
  'fee-flows': 'Fee Flows',
  'api-keys': 'API Keys',
  docs: 'Docs',
  help: 'Help',
  'rate-cards': 'Rate Cards',
  categories: 'Categories',
  'product-categories': 'Product Categories',
  'source-verification': 'Source Verification',
  'fulfillment-audit': 'Fulfillment Audit',
};

// Segments that should not be linkable on their own (parent-only / namespace segments)
const NON_LINKABLE_SEGMENTS = new Set(['admin', 'owner', 'client', 'billing', 'product']);

// Some intermediate segments accumulate to paths that don't exist as routes.
// Map them to the real page instead so the crumb never lands on a blank page.
const LINK_OVERRIDES = {
  '/product-categories': '/admin/products?tab=categories',
};

const HOME_PATHS = {
  client: '/client/dashboard',
  default: '/dashboard',
};

const isLikelyId = (segment) => {
  if (!segment) return false;
  // UUID, ULID, ObjectId, or numeric / mostly-hex IDs
  if (/^[0-9a-f]{8,}$/i.test(segment.replace(/-/g, ''))) return true;
  if (/^\d+$/.test(segment)) return true;
  return false;
};

const prettify = (segment) => {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  // Title-case the segment as a fallback
  return segment
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
};

const Breadcrumbs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stores = useSelector((state) => state.stores?.stores || []);
  const clients = useSelector((state) => state.clients?.clients || []);
  const userRole = useSelector((state) => state.user?.role);
  const selectedStore = useSelector((state) => state.stores?.selectedStore);

  // Async-resolved names for IDs that weren't in Redux (keyed by "parent/id")
  const [resolvedNames, setResolvedNames] = useState({});
  // Async-resolved full client objects for passing to navigation state
  const [resolvedClients, setResolvedClients] = useState({});
  // Re-render when a detail page reports a friendly name for its URL id
  const [nameVersion, setNameVersion] = useState(0);

  useEffect(() => subscribeBreadcrumbNames(() => setNameVersion((v) => v + 1)), []);

  const stateBreadcrumbs = useMemo(
    () => (location.state?.breadcrumbs || []).filter((b) => b.name),
    [location.state],
  );

  // When the URL contains client/store IDs that Redux doesn't know about,
  // fetch them from the cache and store results in local state.
  useEffect(() => {
    if (stateBreadcrumbs.length > 0) return; // explicit breadcrumbs override — no need to resolve

    const segments = location.pathname.split('/').filter(Boolean);
    let needsClientFetch = false;
    let needsStoreFetch = false;

    // Determine which IDs need async resolution
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const prev = segments[i - 1];
      if (!isLikelyId(seg)) continue;
      if (prev === 'clients') {
        const inRedux = clients.find(
          (x) => String(x.id) === String(seg) || String(x.identifierValue) === String(seg),
        );
        if (!inRedux && !resolvedNames[`clients/${seg}`]) needsClientFetch = true;
      } else if (prev === 'stores') {
        const inRedux = stores.find((x) => String(x.storeId) === String(seg));
        if (!inRedux && !resolvedNames[`stores/${seg}`]) needsStoreFetch = true;
      }
    }

    if (!needsClientFetch && !needsStoreFetch) return;

    const promises = [];

    if (needsStoreFetch) {
      promises.push(
        getStoresDataCached().then((res) => {
          const storeList = res?.stores ?? [];
          const updates = {};
          for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const prev = segments[i - 1];
            if (isLikelyId(seg) && prev === 'stores') {
              const s = storeList.find((x) => String(x.storeId) === String(seg));
              if (s?.name) updates[`stores/${seg}`] = s.name;
            }
          }
          return { names: updates, clients: {} };
        }),
      );
    }

    if (needsClientFetch) {
      // Use selectedStore.storeId as the context for fetching clients.
      // Fall back to the first numeric store segment in the URL.
      let clientStoreId = selectedStore?.storeId;
      if (!clientStoreId) {
        for (let i = 0; i < segments.length; i++) {
          if (segments[i - 1] === 'stores' && isLikelyId(segments[i])) {
            clientStoreId = segments[i];
            break;
          }
        }
      }
      if (clientStoreId) {
        promises.push(
          getAdminClientsCached(clientStoreId).then((res) => {
            const clientList = res?.clients ?? [];
            const nameUpdates = {};
            const clientUpdates = {};
            for (let i = 0; i < segments.length; i++) {
              const seg = segments[i];
              const prev = segments[i - 1];
              if (isLikelyId(seg) && prev === 'clients') {
                const c = clientList.find(
                  (x) => String(x.id) === String(seg) || String(x.identifierValue) === String(seg),
                );
                if (c?.name) {
                  nameUpdates[`clients/${seg}`] = c.name;
                  clientUpdates[seg] = c;
                }
              }
            }
            return { names: nameUpdates, clients: clientUpdates };
          }),
        );
      }
    }

    if (promises.length === 0) return;

    let cancelled = false;
    Promise.all(promises).then((results) => {
      if (cancelled) return;
      const mergedNames = {};
      const mergedClients = {};
      results.forEach(({ names, clients: cls }) => {
        Object.assign(mergedNames, names);
        Object.assign(mergedClients, cls);
      });
      if (Object.keys(mergedNames).length > 0) {
        setResolvedNames((prev) => ({ ...prev, ...mergedNames }));
      }
      if (Object.keys(mergedClients).length > 0) {
        setResolvedClients((prev) => ({ ...prev, ...mergedClients }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, stateBreadcrumbs.length, clients, stores, selectedStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    // If the route author passed explicit breadcrumbs via navigate state, prefer them
    if (stateBreadcrumbs.length > 0) return stateBreadcrumbs;

    const crumbs = [];
    let acc = '';
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      acc += `/${seg}`;
      const prev = segments[i - 1];
      let name;
      let clientData = null;

      // A detail page may have reported a friendly name for this segment
      // (keyed by "parentSegment/idSegment" to avoid cross-entity collisions)
      const reported = getBreadcrumbName(`${prev}/${seg}`);
      if (reported) {
        name = reported;
      } else if (isLikelyId(seg)) {
        // Try to resolve a friendly name based on the parent segment
        if (prev === 'stores') {
          const s = stores.find((x) => String(x.storeId) === String(seg));
          name = s?.name || resolvedNames[`stores/${seg}`] || seg;
        } else if (prev === 'clients') {
          const c = clients.find(
            (x) => String(x.id) === String(seg) || String(x.identifierValue) === String(seg),
          );
          const rc = c || resolvedClients[seg];
          name = rc?.name || resolvedNames[`clients/${seg}`] || seg;
          if (rc) clientData = rc;
        } else if (prev === 'invoices' && /^\d+$/.test(seg)) {
          name = `INV-${String(seg).padStart(5, '0')}`;
        } else if (prev === 'orders' && /^\d+$/.test(seg)) {
          name = `Order #${seg}`;
        } else if (prev === 'asn' && /^\d+$/.test(seg)) {
          name = `ASN #${seg}`;
        } else {
          // For other long IDs (UUIDs etc.), truncate to keep breadcrumb readable
          name = seg.length > 12 ? `${seg.slice(0, 8)}…` : seg;
        }
      } else {
        name = prettify(seg);
      }

      // Determine whether this crumb should be a link.
      // Last segment is always non-link (current page). Namespace-only segments
      // like /admin or /owner are never linked.
      const isLast = i === segments.length - 1;
      const isNamespace = NON_LINKABLE_SEGMENTS.has(seg);
      crumbs.push({
        name,
        link: isLast || isNamespace ? null : LINK_OVERRIDES[acc] || acc,
        _clientData: clientData,
      });
    }

    return crumbs;
    // nameVersion re-runs this memo when a detail page reports a friendly name
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    stateBreadcrumbs,
    stores,
    clients,
    resolvedNames,
    resolvedClients,
    nameVersion,
  ]);

  const homePath = userRole === 'client' ? HOME_PATHS.client : HOME_PATHS.default;

  const goTo = (nav, i) => {
    if (!nav.link) return;
    const state = {
      breadcrumbs: breadcrumbs.slice(0, i),
    };
    // Pass already-resolved client entity so the target page can skip its own fetch
    if (nav._clientData) {
      state.vendor = nav._clientData;
      state.storeId = selectedStore?.storeId;
    }
    navigate(nav.link, { state });
  };

  return (
    <div className="d-flex align-items-center px-2 breadcrumbs-row">
      <span
        className="text-decoration-none breadcrumb-home"
        onClick={() => navigate(homePath)}
        role="button">
        <RxHome />
      </span>

      {breadcrumbs.map((b, i) => (
        <React.Fragment key={`${b.name}-${i}`}>
          <span className="slash mx-2">/</span>
          {b.link ? (
            <span className="custom-link" onClick={() => goTo(b, i)}>
              {b.name}
            </span>
          ) : (
            <span className="breadcrumb-current">{b.name}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Breadcrumbs;
